import { IngestionBrandInfo, IngestionJobStatus, IngestionSourcePlatform } from '../../services/integrationApi';
import { ingest } from './ingestionTokens';
import { classifyRunOutcome } from './runOutcome';
import { tokens } from '../../theme/tokens';

export type ConnectorHealth = 'blocked' | 'failing' | 'flapping' | 'never_run' | 'no_products' | 'paused' | 'healthy';

export type ConnectorFilter = 'attention' | 'all' | 'failing' | 'healthy';

export const HEALTH_LABELS: Record<ConnectorHealth, string> = {
  blocked: 'Blocked by site',
  failing: 'Failing',
  flapping: 'Flapping',
  never_run: 'Never run',
  no_products: 'No products',
  paused: 'Paused',
  healthy: 'Healthy',
};

export interface HealthTone {
  dot: string;
  bg: string;
  border: string;
  ink: string;
}

/** Tone table from the handoff. Kept exact: the three failure modes must read apart at a glance. */
export const HEALTH_TONES: Record<ConnectorHealth, HealthTone> = {
  // The site refuses the connector, not the connector failing: caution, with the danger dot so it still reads as stopped.
  blocked: { dot: ingest.color.danger, bg: ingest.color.warningTint, border: tokens.signal.caution.border, ink: ingest.color.warningInk },
  failing: { dot: ingest.color.danger, bg: ingest.color.dangerTintSoft, border: ingest.color.dangerBorder, ink: ingest.color.dangerInk },
  no_products: { dot: ingest.color.warning, bg: ingest.color.warningTint, border: tokens.signal.caution.border, ink: ingest.color.warningInk },
  flapping: { dot: ingest.color.warning, bg: ingest.color.warningTint, border: tokens.signal.caution.border, ink: ingest.color.warningInk },
  never_run: { dot: ingest.color.dim, bg: tokens.signal.neutral.row, border: tokens.signal.neutral.border, ink: ingest.color.bodyAlt },
  paused: { dot: ingest.color.dotMuted, bg: tokens.signal.neutral.row, border: tokens.signal.neutral.border, ink: ingest.color.muted },
  healthy: { dot: ingest.color.success, bg: ingest.color.successTintSoft, border: tokens.signal.positive.border, ink: ingest.color.successInk },
};

/** Recent runs that reached a verdict — pending/running runs say nothing about health. */
export function finishedRunStatus(status?: IngestionJobStatus): status is 'completed' | 'failed' {
  return status === 'completed' || status === 'failed';
}

/** ≥3 failures in the recent window is the flapping threshold from the handoff. */
const FLAPPING_THRESHOLD = 3;

/**
 * Derived, never stored. Order matters — first match wins, per the handoff:
 * paused → blocked → failing → never run → no products → flapping → healthy.
 * Blocked (MOT-767) sits ahead of failing because the failed runs it explains
 * are the site refusing Chrome, and the schedulers are already holding the brand.
 */
export function deriveConnectorHealth(brand: IngestionBrandInfo): ConnectorHealth {
  if (brand.schedule && brand.schedule.isEnabled === false) return 'paused';
  if (brand.siteBlock) return 'blocked';
  if (classifyRunOutcome(brand.lastRun) === 'failed') return 'failing';
  if (!brand.lastRun) return 'never_run';
  if ((brand.stats?.productCount ?? 0) === 0) return 'no_products';

  // A completed run that failed every (or most) checks counts, not just status failed.
  const recentFailures = (brand.recentRuns || []).filter(
    (run) => finishedRunStatus(run.status) && classifyRunOutcome(run) !== 'ok'
  ).length;
  if (recentFailures >= FLAPPING_THRESHOLD) return 'flapping';

  return 'healthy';
}

/** "Attention" is every connector whose health is not Healthy. */
export function needsAttention(health: ConnectorHealth): boolean {
  return health !== 'healthy';
}

export function formatRelativeTime(dateString?: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Never';
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function formatDuration(start?: string, end?: string): string {
  if (!start) return '—';
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : new Date();
  const diffMs = endDate.getTime() - startDate.getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return '—';
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

export function connectorTypeLabel(brand: IngestionBrandInfo): string {
  return brand.scraperType === 'api' || brand.scraperType === 'feed' ? 'API' : 'Scraper';
}

export const SOURCE_PLATFORM_LABELS: Record<IngestionSourcePlatform, string> = {
  shopify: 'Shopify API',
  woocommerce: 'WooCommerce API',
  bigcommerce: 'BigCommerce site',
  magento: 'Magento site',
  brand_api: 'Brand API',
  website: 'Website scraper',
};

/** Compact form for the sidebar row, where width is tight. */
export const SOURCE_PLATFORM_SHORT_LABELS: Record<IngestionSourcePlatform, string> = {
  shopify: 'Shopify',
  woocommerce: 'WooCommerce',
  bigcommerce: 'BigCommerce',
  magento: 'Magento',
  brand_api: 'Brand API',
  website: 'Website',
};

export function connectorSourceShortLabel(brand: IngestionBrandInfo): string {
  if (brand.apiConnectorId) return 'Vendor API';
  if (brand.sourcePlatform) return SOURCE_PLATFORM_SHORT_LABELS[brand.sourcePlatform];
  return connectorTypeLabel(brand);
}

/**
 * Where the connector reads its catalog from: "Vendor API" when a vendor API
 * connector feeds the brand, then the storefront platform the scraper reads
 * ("Shopify API", "WooCommerce API", ...). Each entry is one source, so a
 * brand fed both ways lists both.
 */
export function connectorSources(brand: IngestionBrandInfo): string[] {
  const sources: string[] = [];
  if (brand.apiConnectorId) sources.push('Vendor API');
  if (brand.sourcePlatform) sources.push(SOURCE_PLATFORM_LABELS[brand.sourcePlatform]);
  if (sources.length === 0) sources.push(connectorTypeLabel(brand));
  return sources;
}

/**
 * Header meta reads the sources ("Shopify API", "Vendor API + BigCommerce site"),
 * plus " · Beta" when the connector is `ready`.
 * Only `ready` earns Beta — it is built but unproven. `coming_soon` is not built
 * at all, and labelling it Beta would imply a connector an operator could run.
 */
export function connectorMethodLabel(brand: IngestionBrandInfo): string {
  const base = connectorSources(brand).join(' + ');
  return brand.scraperStatus === 'ready' ? `${base} · Beta` : base;
}

/** Short text mark for the 44px brand tile when no logo resolves. */
export function brandMark(name: string): string {
  const words = String(name || '?')
    .replace(/\(.*?\)/g, ' ')
    .split(/[\s/·-]+/)
    .filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 4).toUpperCase();
  return words.slice(0, 3).map((word) => word[0]).join('').toUpperCase();
}

/** "Oct 8", in the viewer's locale: the dates the blocked banner and rail row name. */
export function formatShortDate(dateString?: string | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function sidebarSubtitle(brand: IngestionBrandInfo, health: ConnectorHealth): string {
  const when = formatRelativeTime(brand.lastRun?.completedAt || brand.lastRun?.startedAt);
  if (health === 'healthy') return `Success · ${when}`;
  if (health === 'blocked' && brand.siteBlock) {
    return `${HEALTH_LABELS[health]} · next try ${formatShortDate(brand.siteBlock.nextProbeAt)}`;
  }
  if (health === 'never_run') {
    return brand.schedule?.nextScheduledRun
      ? `Never run · scheduled ${new Date(brand.schedule.nextScheduledRun).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}`
      : 'Never run';
  }
  if (health === 'paused' && !brand.lastRun) return 'Paused';
  return `${HEALTH_LABELS[health]} · ${when}`;
}

const FREQUENCY_LABELS: Record<string, string> = {
  manual: 'Manual',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

/**
 * Whether the scheduled price refresh runs for this connector. A paused schedule
 * runs nothing, whatever the price-refresh switch says; mirrors
 * `isPriceRefreshEnrolled` in the API's scraperSchedulingService.
 */
export function isPriceRefreshOn(schedule: IngestionBrandInfo['schedule']): boolean {
  if (!schedule || schedule.isEnabled !== true) return false;
  return schedule.priceRefreshEnabled !== false;
}

/** Matches `scheduledPriceRefresh` cron `0 4 * * *` UTC. */
export const PRICE_REFRESH_SCHEDULE_LABEL = 'Daily · 4:00 AM UTC';

/**
 * Reads the same way the health model does: only an explicitly disabled schedule
 * is Paused. A connector with no schedule at all has never been configured, and
 * saying "Paused" there contradicts a rail row that reads Healthy or Never run.
 */
export function formatScheduleLine(brand: IngestionBrandInfo): string {
  const schedule = brand.schedule;
  if (!schedule) return 'Not scheduled';
  if (schedule.isEnabled === false) return 'Paused';
  const freq = FREQUENCY_LABELS[schedule.frequency] || schedule.frequency;
  const time = schedule.preferredTime || '';
  const tz = schedule.timezone || 'UTC';
  return time ? `${freq} · ${time} ${tz}` : `${freq} · ${tz}`;
}
